/**
 * deriveTheme — one palette engine for web + mobile.
 *
 * Instead of N hand-tuned themes, a palette is described by just three knobs:
 *   - `accent`  — the brand colour (the "Accent" slider)
 *   - `base`    — a neutral seed whose hue/chroma tints every grey (the "Base"
 *                 slider); near-grey = neutral, tinted = warm/sage/violet, etc.
 *   - `mode`    — 'light' | 'dark' (sets the neutral lightness ramp)
 *
 * Everything else (surfaces, hovers, borders, soft tints) is DERIVED in oklab,
 * the same way HeroUI's `color-mix(in oklab, …)` tokens are. The four shipped
 * themes (dark / parchment / eucalyptus / smoke) are just presets — pairs of
 * (accent, base, mode) — so users keep them AND can dial their own.
 *
 * Output is a canonical {@link DerivedPalette}; each app maps it onto its own
 * token names (web CSS vars / mobile PaletteColors) in a thin adapter.
 */
import { rgbToOklch, oklchToHex, mixHex, parseHex, contrastRatio, hexToRgba } from './color.js';

export type ThemeMode = 'light' | 'dark';

export interface ThemeInput {
  /** Brand colour, hex. */
  accent: string;
  /** Neutral seed, hex — its hue/chroma tints the greys. */
  base: string;
  mode: ThemeMode;
  /**
   * How light the background is, 0..1, within the mode's usable range (dark:
   * near-black → slate; light: warm paper → near-white). Absent = the fixed
   * preset ramp, so presets and palettes saved before this knob look the same.
   * When present, the base's saturation also tints more strongly (up to
   * {@link TINT_MAX}), because the user is now choosing a background colour,
   * not a faint hue hint.
   */
  brightness?: number;
  /** How far cards and panels step off the background, 0..1 (0 = almost flush,
   *  1 = clearly raised). Only used together with `brightness`. */
  cards?: number;  /**
   * The caller picks the text colour on the accent itself (dark text on a light
   * accent), so the accent must NOT be darkened for white text. Without this a
   * light accent such as copper came back as a different, muddier colour than
   * the one the user picked. The accent is still kept visible against cards.
   */
  keepAccent?: boolean;
}

/** Usable canvas lightness per mode for the `brightness` knob. Outside these the
 *  palette stops being a dark (or light) theme: text contrast and the meaning of
 *  "raised" break down. */
export const BRIGHTNESS_RANGE = { dark: [0.1, 0.32], light: [0.86, 0.99] } as const;
/** Max tint strength a chosen background colour may carry, per mode. */
export const TINT_MAX = { dark: 0.075, light: 0.045 } as const;

/** The `brightness` value that reproduces the preset ramp's canvas. */
export function defaultBrightness(mode: ThemeMode): number {
  const [lo, hi] = BRIGHTNESS_RANGE[mode];
  const canvas = mode === 'dark' ? DARK_ANCHORS.canvas : LIGHT_ANCHORS.canvas;
  return (canvas - lo) / (hi - lo);
}
export const DEFAULT_CARDS = 0.5;

export interface DerivedPalette {
  mode: ThemeMode;
  isLight: boolean;
  /** Outermost background. */
  canvas: string;
  /** Rail / behind-surface background. */
  outer: string;
  /** Default card / row surface. */
  surface: string;
  /** Elevated surface (modals, popovers, raised cards). */
  surfaceRaised: string;
  /** Recessed surface (sunken trays). */
  surfaceSunken: string;
  /** Secondary surface (sections, inputs). */
  surfaceSecondary: string;
  /** Solid row hover/press background. */
  hover: string;
  /** Incoming chat bubble. */
  bubbleIn: string;
  /** Hairline border/separator (rgba — carries its own alpha). */
  hairline: string;
  ink: string;
  inkMuted: string;
  inkFaint: string;
  /** Raw accent — fills, rings, active states. */
  accent: string;
  /** Accent hover (shifted toward the surface direction). */
  accentHover: string;
  /** Accent adjusted to meet AA contrast as TEXT on `surface`. */
  accentText: string;
  success: string;
  warning: string;
  danger: string;
  secondary: string;
  white: string;
  /** Base hex for neutral elevation tints (web `bg-white/N`): warm-near-white on
   *  dark, a dark base-tint on light — so the same tint reads on any palette. */
  overlayTint: string;
}

interface NeutralAnchors {
  canvas: number;
  outer: number;
  surface: number;
  surfaceRaised: number;
  surfaceSunken: number;
  surfaceSecondary: number;
  hover: number;
  bubbleIn: number;
  ink: number;
  inkMuted: number;
  inkFaint: number;
  /** Max chroma a neutral may carry from the base hue (the tint strength). */
  tintChroma: number;
  /** Alpha for the hairline border. */
  hairlineAlpha: number;
  /** Lightness for the elevation-tint base. */
  overlayTintL: number;
}

// Lightness ramps tuned to match the existing hand-built palettes so the
// presets look ~identical to today; the base hue rotates the neutrals.
const DARK_ANCHORS: NeutralAnchors = {
  // Darker than before — a near-black canvas (was a mid-grey ~0.18) so dark
  // reads as proper dark, and the Base tint shows on the surfaces above it.
  canvas: 0.155,
  outer: 0.155,
  surface: 0.215,
  surfaceRaised: 0.245,
  surfaceSunken: 0.125,
  surfaceSecondary: 0.215,
  hover: 0.27,
  bubbleIn: 0.215,
  ink: 0.985,
  inkMuted: 0.7,
  inkFaint: 0.52,
  // Tint strength: how much of the Base hue every neutral carries. Generous so
  // picking a warm/cool/green Base visibly tints the WHOLE surface set (canvas,
  // cards, rows) — not just the accent.
  tintChroma: 0.045,
  hairlineAlpha: 0.08,
  overlayTintL: 0.95,
};

const LIGHT_ANCHORS: NeutralAnchors = {
  canvas: 0.95,
  outer: 0.93,
  // Surfaces pulled OFF pure white (1.0 is achromatic — it could never take a
  // tint, which is why cards stayed white while only the accent changed). At
  // ~0.97 + a strong tintChroma the Base hue reads clearly on cards/rows.
  surface: 0.965,
  surfaceRaised: 0.985,
  surfaceSunken: 0.91,
  surfaceSecondary: 0.945,
  hover: 0.9,
  bubbleIn: 0.945,
  ink: 0.3,
  inkMuted: 0.5,
  inkFaint: 0.66,
  tintChroma: 0.052,
  hairlineAlpha: 0.13,
  overlayTintL: 0.2,
};

// Status colours stay constant per mode — red must read as red regardless of
// the accent/base the user picks. (Brand-tinting these hurts recognisability.)
const STATUS = {
  dark: { success: '#17c964', warning: '#f7b750', danger: '#db3b3e', secondary: '#7828c8' },
  light: { success: '#2f8d57', warning: '#b8801c', danger: '#c0492f', secondary: '#7828c8' },
} as const;

const AA_CONTRAST = 4.5;
// Min contrast of WHITE on the accent fill. Low enough that normal saturated
// accents (incl. all four presets) pass untouched; high enough that a white /
// pastel pick gets darkened so white-on-accent text + icons stay visible.
const ON_ACCENT_CONTRAST = 2.6;
// Min contrast of the accent against the SURFACE, so accent-coloured text/icons/
// fills stay distinguishable from the background. Without this, a near-black
// accent on a dark theme (or near-white on light) blends into the surface and
// `text-accent` badges/labels vanish. Low enough that the four presets pass
// untouched; only accents too close to the surface get nudged away.
const ACCENT_SURFACE_CONTRAST = 2.7;

/** A neutral at lightness `L`, tinted by the base hue up to `tintChroma`. */
function neutral(L: number, baseHue: number, baseChroma: number, tintChroma: number): string {
  return oklchToHex({ L, C: Math.min(baseChroma, tintChroma), h: baseHue });
}

/** Darken an accent until WHITE text/icons read on it (≥ `ON_ACCENT_CONTRAST`).
 *  Normal saturated accents pass untouched; only very light picks (white,
 *  pastels) get pulled down — so the app's many `text-white on accent` spots
 *  (buttons, "mine" bubbles, the vault glyph) never vanish. */
function clampAccentFill(accent: string): string {
  // Already readable → return the exact input (no oklch round-trip drift).
  if (contrastRatio('#ffffff', accent) >= ON_ACCENT_CONTRAST) return accent;
  const { L, C, h } = rgbToOklch(parseHex(accent));
  let l = L;
  for (let i = 0; i < 28; i++) {
    const hex = oklchToHex({ L: l, C, h });
    if (contrastRatio('#ffffff', hex) >= ON_ACCENT_CONTRAST) return hex;
    l -= 0.03;
    if (l <= 0) break;
  }
  return oklchToHex({ L: Math.max(0, l), C, h });
}

/** Push the accent's lightness AWAY from the surface until it's clearly visible
 *  against it (≥ ACCENT_SURFACE_CONTRAST) — lighten on dark, darken on light.
 *  Keeps a near-black-on-dark (or near-white-on-light) accent from blending into
 *  the background. No-op for accents already distinct enough (incl. the presets). */
function ensureVsSurface(accent: string, surface: string, isLight: boolean): string {
  if (contrastRatio(accent, surface) >= ACCENT_SURFACE_CONTRAST) return accent;
  const { L, C, h } = rgbToOklch(parseHex(accent));
  let l = L;
  for (let i = 0; i < 28; i++) {
    const hex = oklchToHex({ L: l, C, h });
    if (contrastRatio(hex, surface) >= ACCENT_SURFACE_CONTRAST) return hex;
    l += isLight ? -0.03 : 0.03;
    if (l <= 0 || l >= 1) break;
  }
  return oklchToHex({ L: Math.max(0, Math.min(1, l)), C, h });
}

/** Nudge `color`'s lightness until it hits AA contrast as text on `bg`
 *  (darken on light surfaces, lighten on dark). Bounded so it always returns. */
function clampForText(color: string, bg: string, isLight: boolean): string {
  const { L, C, h } = rgbToOklch(parseHex(color));
  let l = L;
  for (let i = 0; i < 24; i++) {
    const hex = oklchToHex({ L: l, C, h });
    if (contrastRatio(hex, bg) >= AA_CONTRAST) return hex;
    l += isLight ? -0.03 : 0.03;
    if (l <= 0 || l >= 1) break;
  }
  return oklchToHex({ L: Math.max(0, Math.min(1, l)), C, h });
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/**
 * Neutral anchors for an explicit brightness / cards choice. The ramp keeps the
 * preset's SHAPE (sunken below canvas, cards above, hover above cards) but is
 * re-centred on the chosen canvas, and text lightness is solved for contrast
 * against the resulting cards instead of being fixed — so a lighter dark theme
 * or a darker light one still reads.
 */
function anchorsFor(input: ThemeInput, base: NeutralAnchors): NeutralAnchors {
  const isLight = input.mode === 'light';
  const [lo, hi] = BRIGHTNESS_RANGE[input.mode];
  const canvas = lo + (hi - lo) * clamp01(input.brightness ?? defaultBrightness(input.mode));
  const cards = clamp01(input.cards ?? DEFAULT_CARDS);
  if (!isLight) {
    const lift = 0.02 + 0.08 * cards;
    const surface = canvas + lift;
    return {
      ...base,
      canvas,
      outer: canvas,
      surface,
      surfaceRaised: surface + 0.03,
      surfaceSunken: Math.max(0.04, canvas - 0.03),
      surfaceSecondary: surface,
      hover: surface + 0.055,
      bubbleIn: surface,
      tintChroma: TINT_MAX.dark,
    };
  }
  // Light: cards are lighter than the page while there is room above it; on a
  // near-white page they step DOWN instead, so they never vanish into it.
  const lift = 0.006 + 0.04 * cards;
  const up = canvas + lift <= 0.995;
  const surface = up ? canvas + lift : canvas - lift;
  return {
    ...base,
    canvas,
    outer: canvas - 0.02,
    surface,
    surfaceRaised: up ? Math.min(0.998, surface + 0.02) : Math.min(0.998, canvas + 0.004),
    surfaceSunken: canvas - 0.04,
    surfaceSecondary: surface - 0.02,
    hover: surface - 0.06,
    bubbleIn: surface - 0.02,
    tintChroma: TINT_MAX.light,
  };
}

/** Pull a text neutral's lightness away from `bg` until it reaches `min`
 *  contrast (lighter on dark, darker on light). */
function solveInk(L: number, min: number, bg: string, h: number, c: number, isLight: boolean): string {
  let l = L;
  for (let i = 0; i < 40; i++) {
    const hex = oklchToHex({ L: l, C: c, h });
    if (contrastRatio(hex, bg) >= min) return hex;
    l += isLight ? -0.02 : 0.02;
    if (l <= 0 || l >= 1) break;
  }
  return oklchToHex({ L: clamp01(l), C: c, h });
}

export function deriveTheme(input: ThemeInput): DerivedPalette {
  const isLight = input.mode === 'light';
  const tuned = input.brightness !== undefined || input.cards !== undefined;
  const A = tuned
    ? anchorsFor(input, isLight ? LIGHT_ANCHORS : DARK_ANCHORS)
    : isLight
      ? LIGHT_ANCHORS
      : DARK_ANCHORS;
  const baseOklch = rgbToOklch(parseHex(input.base));
  const n = (L: number) => neutral(L, baseOklch.h, baseOklch.C, A.tintChroma);

  const status = isLight ? STATUS.light : STATUS.dark;
  const surface = n(A.surface);
  // A tuned palette solves its text against the cards it actually produced: a
  // lighter dark theme or a greyer light one would otherwise lose contrast.
  const inkChroma = Math.min(baseOklch.C, A.tintChroma) * 0.3;
  const ink = tuned ? solveInk(A.ink, 7, surface, baseOklch.h, inkChroma, isLight) : n(A.ink);
  const inkMuted = tuned ? solveInk(A.inkMuted, 4.5, surface, baseOklch.h, inkChroma, isLight) : n(A.inkMuted);
  const inkFaint = tuned ? solveInk(A.inkFaint, 3, surface, baseOklch.h, inkChroma, isLight) : n(A.inkFaint);
  // Accent: first push it clear of the surface (so accent text/icons/fills don't
  // blend into the background), then ensure white still reads on the fill.
  const visible = ensureVsSurface(input.accent, surface, isLight);
  const accent = input.keepAccent ? visible : clampAccentFill(visible);
  const accentHover = mixHex(accent, isLight ? '#000000' : '#ffffff', 0.12);
  const overlayTint = oklchToHex({
    L: A.overlayTintL,
    C: Math.min(baseOklch.C, A.tintChroma * 1.5),
    h: baseOklch.h,
  });

  return {
    mode: input.mode,
    isLight,
    canvas: n(A.canvas),
    outer: n(A.outer),
    surface,
    surfaceRaised: n(A.surfaceRaised),
    surfaceSunken: n(A.surfaceSunken),
    surfaceSecondary: n(A.surfaceSecondary),
    hover: n(A.hover),
    bubbleIn: n(A.bubbleIn),
    hairline: hexToRgba(isLight ? '#000000' : '#ffffff', A.hairlineAlpha),
    ink,
    inkMuted,
    inkFaint,
    accent,
    accentHover,
    accentText: clampForText(accent, surface, isLight),
    success: status.success,
    warning: status.warning,
    danger: status.danger,
    secondary: status.secondary,
    white: '#ffffff',
    overlayTint,
  };
}

export interface ThemePreset {
  id: string;
  /** Stable fallback label (localised at call sites). */
  label: string;
  accent: string;
  base: string;
  mode: ThemeMode;
}

/** The four shipped looks, now expressed as (accent, base, mode) seeds. */
export const THEME_PRESETS: ThemePreset[] = [
  { id: 'dark', label: 'Тёмная', accent: '#c89d74', base: '#171719', mode: 'dark' },
  { id: 'parchment', label: 'Светлая', accent: '#9a6544', base: '#777777', mode: 'light' },
  { id: 'eucalyptus', label: 'Эвкалипт', accent: '#3f7d63', base: '#3d5a40', mode: 'light' },
  { id: 'smoke', label: 'Дымка', accent: '#6c4bd1', base: '#4a3a6b', mode: 'light' },
];

export const DEFAULT_PRESET = THEME_PRESETS[0]!;
